/* eslint-disable react-refresh/only-export-components */
/**
 * Settings context for admin display preferences.
 *
 * @module context/SettingsContext
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

const STORAGE_KEY = 'clarity-admin:hide-internal-stats';

interface SettingsContextType {
  hideInternalStats: boolean;
  setHideInternalStats: (value: boolean) => void;
}

const SettingsContext = createContext<SettingsContextType | null>(null);

/**
 * Settings provider for display preferences.
 * Persists hide-internal-stats toggle to localStorage.
 */
export function SettingsProvider({ children }: { children: ReactNode }) {
  const [hideInternalStats, setHideInternalStatsState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const setHideInternalStats = useCallback((value: boolean) => {
    setHideInternalStatsState(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  return (
    <SettingsContext.Provider value={{ hideInternalStats, setHideInternalStats }}>
      {children}
    </SettingsContext.Provider>
  );
}

/**
 * Hook to access display settings.
 *
 * @returns Settings context
 * @throws Error if used outside SettingsProvider
 */
export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
